import { BranchStepTypes } from 'pages/AutomationEngine/types/automation';
import {
  isBranchStep,
  isGoalOrCondition,
  isLoopStep,
  isLoopStepNext,
} from './ts-utilities';

const findLoopEnd = (tree, parentKey) => {
  if (!tree) {
    return undefined;
  }

  const root = tree.find((step) => step.id === parentKey);

  if (!root) {
    return undefined;
  }
  const nextKey = tree.find(
    (step) => step.parentKey === parentKey && isLoopStepNext(step)
  );

  if (!nextKey) {
    return undefined;
  }

  let loopEnd = undefined;
  const queue = [root];

  while (queue.length > 0) {
    const current = queue.shift();

    let children = tree.filter(
      (step) => step.id !== nextKey.id && step.parentKey === current.id
    );

    if (!children || children.length === 0) {
      loopEnd = current;
      break;
    } else {
      if (isLoopStep(current) && current.id !== root.id) {
        // skip past this included loop

        const loopNexStep = tree.find(
          (step) => step.parentKey === current.id && isLoopStepNext(step)
        );
        children = tree.filter((step) => step.parentKey === loopNexStep.id);
        if (!children.length) {
          // no children of merge then the merge is the end
          loopEnd = loopNexStep;
        }
      } else if (
        (isGoalOrCondition(current) || isBranchStep(current)) &&
        current.isBranchGroupInitiator &&
        current.id !== parentKey
      ) {
        // going to skip this whole merged branch
        const sourceStep = tree.find(
          (step) =>
            step.parentKey === current.id && step.type === BranchStepTypes.MERGE
        );

        children = tree.filter(
          (step) =>
            step.parentKey === sourceStep.id &&
            step.type !== BranchStepTypes.MERGE
        );

        if (!children.length) {
          // no children of merge then the merge is the end
          loopEnd = sourceStep;
        }
      }
    }

    queue.push(...children);
  }
  return loopEnd;
};

const getLoopEnds = (tree) => {
  const loopInitiatorNodes = tree.filter(isLoopStep);

  if (loopInitiatorNodes.length === 0) {
    return [];
  }

  const ends = loopInitiatorNodes.map((li) => {
    const end = findLoopEnd(tree, li.id);
    const join = tree.find(
      (step) => step.parentKey === li.id && isLoopStepNext(step)
    );
    return { initiatorId: li.id, end, join };
  });

  return ends;
};

export const addLoopDepthToSteps = (steps) => {
  const loopEndSteps = getLoopEnds(steps);
  const loopJoinIds = loopEndSteps.reduce(
    (prevLoopEndId, endStep) => ({
      ...prevLoopEndId,
      [endStep.join.id]: endStep,
    }),
    {}
  );

  const childrenByParent = steps.reduce((acc, step) => {
    const key = step.parentKey ?? null;
    (acc[key] ??= []).push(step);
    return acc;
  }, {});

  const loopContextById = {};
  const visited = new Set();

  // Walk the tree, not the array: a step is inside a loop only when that loop
  // is one of its ancestors. Walking in array order instead would sweep in
  // anything that merely sits between a Loop and its Next/Finish - such as the
  // "no" path of a condition whose "yes" path holds the loop. At this point a
  // Next/Finish step is still a direct child of its Loop, so the loop closes on
  // the way down and only the body carries the extra depth.
  const walk = (step, depth, variables) => {
    if (visited.has(step.id)) {
      return;
    }
    visited.add(step.id);

    let nextDepth = depth;
    let nextVariables = variables;

    if (isLoopStep(step)) {
      nextDepth++;
      nextVariables = [
        {
          loopStepId: step.id,
          variableName: step?.details?.forEachItem?.arrayVariable?.label,
        },
        ...variables,
      ];
    }

    if (isLoopStepNext(step)) {
      nextDepth--;
      nextVariables = variables.slice(1);
    }

    loopContextById[step.id] = {
      loopDepth: nextDepth,
      loopVariables: nextVariables,
    };

    (childrenByParent[step.id] ?? []).forEach((child) =>
      walk(child, nextDepth, nextVariables)
    );
  };

  const stepIds = new Set(steps.map(({ id }) => id));
  steps
    .filter((step) => !step.parentKey || !stepIds.has(step.parentKey))
    .forEach((root) => walk(root, 0, []));

  // point loop next steps at the end of the loop body
  return steps.map((step) => {
    const { loopDepth = 0, loopVariables = [] } =
      loopContextById[step.id] ?? {};

    const nextStep = { ...step, loopDepth, loopVariables: [...loopVariables] };

    return loopJoinIds[nextStep.id]
      ? { ...nextStep, parentKey: loopJoinIds[nextStep.id].end.id }
      : nextStep;
  });
};
